import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import {
  rentersPolicyAvailable,
  editableHouseholdMembersStates,
  driverCarAssignmentStates,
  editableResidenceInsuranceType,
  isResidentialOwnerStates
} from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';

import { useStore } from 'core/store';
import { shouldOfferRentersOnAuto } from 'core/helpers/renters-on-auto';
import { AuthContext } from 'core/components/auth';
import FormList from 'core/components/form-list';
import { useDisabledState } from 'common/disabled-context';
import CarAssignment from 'common/components/auto/car-assignment';
import { AddLienHoldersForm, LienHolders } from 'common/components/people';
import { interestedPartyListLabels } from 'common/helpers/auto-interested-party-helpers';

import { Coverage } from './auto-coverage';
import Cars from './cars';
import Trailers from './trailers';
import Renters from './renters';
import PriorCoverage from './prior-coverage';
import AutoPolicyDetails from './auto-policy-details';
import CompanionPolicy from './companion-policy';
import PolicyLevelAdjustment from './policy-level-adjustment';
import { ResidenceInsuranceType } from './residence-insurance-type';

function AutoOffer({ section = 'all' }) {
  const { values } = useFormikContext();
  const { offer } = useStore();
  const { viewOnly } = useContext(AuthContext);
  const { disabled } = useDisabledState();
  const { state, selectedOption } = offer;

  const showAutoPolicyDetails = editableHouseholdMembersStates.includes(state);

  // Show renters endorsement toggle when Auto is part of the selected option (Auto or HA bundle)
  // When looking at renters options, the renters details will appear in a new Tab at top of offer
  // so that's why we're not including that as part of the rendering logic here in Auto Details.
  // Attaching Renters to an Auto policy is Branch-only (#35383) — CFM never sold it, so keep the
  // toggle out of the new-offer flow too, not just the policy Details view.
  const showRenters =
    shouldOfferRentersOnAuto() &&
    rentersPolicyAvailable[state] &&
    (selectedOption === policyTypes.Auto || selectedOption === policyTypes.HABundle) &&
    values.offerings.offerRenters;

  const showResidenceInsuranceTypeSelector =
    selectedOption === policyTypes.Auto &&
    (editableResidenceInsuranceType.includes(state) || isResidentialOwnerStates.includes(state));

  return (
    <>
      <Cars disabled={disabled} section={section} />
      {section !== 'coverage' && (
        <>
          {driverCarAssignmentStates[state] && <CarAssignment state={state} />}
          <Trailers disabled={disabled} offer={offer} compact={section !== 'all'} />
          {showResidenceInsuranceTypeSelector && <ResidenceInsuranceType disabled={disabled} />}
        </>
      )}
      {section !== 'details' && <Coverage disabled={disabled} offer={offer} />}
      {section !== 'coverage' && (
        <>
          {showAutoPolicyDetails && <AutoPolicyDetails disabled={disabled} />}
          <CompanionPolicy disabled={disabled} />
          <PolicyLevelAdjustment disabled={disabled} />
          <PriorCoverage />
        </>
      )}
      {section !== 'details' && (
        <>
          {showRenters && <Renters disabled={disabled} />}
          <FormList
            key={`lien-holders-${disabled}`}
            id="lienHolders"
            item={LienHolders}
            addForm={AddLienHoldersForm}
            {...interestedPartyListLabels()}
            disabled={disabled || viewOnly}
          />
        </>
      )}
    </>
  );
}

AutoOffer.propTypes = { section: PropTypes.oneOf(['all', 'details', 'coverage']) };

export default observer(AutoOffer);
